import { useState } from 'react';
import DownIcon from 'assets/icons/down.svg';
import DownWhiteIcon from 'assets/icons/down-white.svg';
import { useDarkMode } from 'utils/hooks/DarkMode';
import { SavedUserDataProps } from 'interfaces/components/savedUserData';
import { UserHistoricalFormDataItem } from 'interfaces/components/savedUserData/UserHistoricalFormDataItem';
import { formatDateWithTime } from 'utils/helpers/dataFormatters';

const SavedUserData = ({
  userHistoricalFormData,
  isLoading,
  onDataSelect
}: SavedUserDataProps) => {
  const isDarkMode = useDarkMode('(prefers-color-scheme: dark)');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedData = [...userHistoricalFormData].sort((a, b) => {
    const dateA = new Date(a.DW_LAST_UPDT);
    const dateB = new Date(b.DW_LAST_UPDT);
    return sortDirection === 'desc'
      ? dateB.getTime() - dateA.getTime()
      : dateA.getTime() - dateB.getTime();
  });

  const handleSortToggle = () => {
    setSortDirection(prev => (prev === 'desc' ? 'asc' : 'desc'));
  };

  const handleDataClick = (entry: UserHistoricalFormDataItem) => {
    onDataSelect?.(entry);
  };

  return (
    <div className='w-full flex flex-col items-start gap-4 px-8 py-6'>
      <h2 className='text-2xl'>Saved Data</h2>

      {isLoading ? (
        <div className='w-full h-[340px] bg-white dark:bg-black grid place-content-center text-center'>
          <div className='animate-spin h-16 w-16 border-[6px] border-[#d7d6d5] border-t-green-400 rounded-full'></div>
          <p className='mt-4'>Loading...</p>
        </div>
      ) : (
        <div className='w-full border border-gray-200 dark:border-gray-700 rounded-lg'>
          <div
            className='flex items-center gap-1 w-full px-3 py-[14px] bg-gray-200 dark:bg-gray-800 cursor-pointer'
            onClick={handleSortToggle}
          >
            <span className='text-xs font-semibold'>DATE</span>
            <img
              src={isDarkMode ? DownWhiteIcon : DownIcon}
              alt='sort'
              className={`w-4 h-4 transition-transform duration-200 ${
                sortDirection === 'asc' ? 'rotate-180' : ''
              }`}
            />
          </div>

          <div className='max-h-[300px] overflow-y-auto'>
            {sortedData.map((entry, index) => (
              <div
                key={`${entry.DW_LAST_UPDT}-${index}`}
                className={`px-3 py-[13px] border-t border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 ${
                  index === sortedData.length - 1 ? 'border-b-0' : ''
                }`}
                onClick={() => handleDataClick(entry)}
              >
                <span className='text-sm underline hover:text-green-700 transition-colors duration-150'>
                  {formatDateWithTime(entry.DW_LAST_UPDT)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedUserData;
